// ** React Imports
import { ChangeEvent, MouseEvent, useState } from 'react';

// ** MUI Imports
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';

// ** Icons Imports
import EyeOutline from 'mdi-material-ui/EyeOutline';
import KeyOutline from 'mdi-material-ui/KeyOutline';
import EyeOffOutline from 'mdi-material-ui/EyeOffOutline';
import LockOpenOutline from 'mdi-material-ui/LockOpenOutline';

// ** Custom Components Imports
import CustomAvatar from 'src/materio/@core/components/mui/avatar';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Control, Controller, FieldErrors } from 'react-hook-form';
import { FormHelperText } from '@mui/material';
import { client } from '../../../utils/axios-client';
import { userChangePasswordEndpoint } from '../../../configs/endpoints';
import ApplicationStore from '../../../stores/ApplicationStore';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ApiKeyManager from '../../../v2/features/Api/ApiKeyManager/ApiKeyManager';
import StandardButton from '../../../v2/components/Button/StandardButton';

const schema = yup.object().shape({
    newPassword: yup.string().required('Password is required'),
    currentPassword: yup.string().required('Current password is required'),
    confirmNewPassword: yup.string().oneOf([yup.ref('newPassword'), null], 'Passwords must match'),
});

interface State {
    showNewPassword: boolean;
    showCurrentPassword: boolean;
    showConfirmNewPassword: boolean;
}

const defaultValues = {
    newPassword: '',
    currentPassword: '',
    showNewPassword: false,
    confirmNewPassword: '',
    showCurrentPassword: false,
    showConfirmNewPassword: false,
};

const TabSecurity = () => {
    // ** States
    const [values, setValues] = useState<State>({
        showNewPassword: false,
        showCurrentPassword: false,
        showConfirmNewPassword: false,
    });

    const {
        reset,
        control,
        setError,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: defaultValues,
        mode: 'onChange',
        resolver: yupResolver(schema),
    });

    const handleClickShowCurrentPassword = () => {
        setValues({ ...values, showCurrentPassword: !values.showCurrentPassword });
    };
    const handleMouseDownCurrentPassword = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleClickShowNewPassword = () => {
        setValues({ ...values, showNewPassword: !values.showNewPassword });
    };
    const handleMouseDownNewPassword = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    // Handle Confirm New Password
    const handleClickShowConfirmNewPassword = () => {
        setValues({ ...values, showConfirmNewPassword: !values.showConfirmNewPassword });
    };
    const handleMouseDownConfirmNewPassword = (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const onSave = async (data: any) => {
        try {
            const response = await client.post(userChangePasswordEndpoint, data);

            if (response.data.succeeded) {
                ApplicationStore.update((s) => {
                    s.snackbarMessage = 'Password changed';
                    s.snackbarOpen = true;
                });

                reset(defaultValues);
            } else {
                if (response.data.errors[0].code === 'PasswordMismatch') {
                    setError('currentPassword', { type: 'string', message: 'Current password is not correct' });
                } else {
                    ApplicationStore.update((s) => {
                        s.snackbarMessage = 'Error changing password';
                        s.snackbarOpen = true;
                    });
                }
            }
        } catch (err) {
            ApplicationStore.update((s) => {
                s.snackbarMessage = 'Error changing password';
                s.snackbarOpen = true;
            });
        }
    };

    return (
        <form>
            <CardContent sx={{ pb: 0 }}>
                <Grid container spacing={5}>
                    <Grid item xs={12} sm={6}>
                        <Grid container spacing={5}>
                            <Grid item xs={12} sx={{ mt: 4.75 }}>
                                <Controller
                                    name="currentPassword"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange, onBlur } }) => (
                                        <FormControl fullWidth size="small">
                                            <InputLabel htmlFor="account-settings-current-password">
                                                Current Password
                                            </InputLabel>
                                            <OutlinedInput
                                                size="small"
                                                label="Current Pasword"
                                                value={value}
                                                id="account-settings-current-password"
                                                type={values.showCurrentPassword ? 'text' : 'password'}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowCurrentPassword}
                                                            onMouseDown={handleMouseDownCurrentPassword}
                                                        >
                                                            {values.showCurrentPassword ? (
                                                                <EyeOutline />
                                                            ) : (
                                                                <EyeOffOutline />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>
                                    )}
                                />
                                {errors.currentPassword && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.currentPassword.message}
                                    </FormHelperText>
                                )}
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 6 }}>
                                <Controller
                                    name="newPassword"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange, onBlur } }) => (
                                        <FormControl fullWidth size="small">
                                            <InputLabel htmlFor="account-settings-new-password">
                                                New Password
                                            </InputLabel>
                                            <OutlinedInput
                                                size="small"
                                                label="New Pasword"
                                                value={value}
                                                id="account-settings-new-password"
                                                type={values.showNewPassword ? 'text' : 'password'}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowNewPassword}
                                                            onMouseDown={handleMouseDownNewPassword}
                                                        >
                                                            {values.showNewPassword ? (
                                                                <EyeOutline />
                                                            ) : (
                                                                <EyeOffOutline />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>
                                    )}
                                />
                                {errors.newPassword && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.newPassword.message}
                                    </FormHelperText>
                                )}
                            </Grid>

                            <Grid item xs={12} sx={{ mt: 6, mb: 6 }}>
                                <Controller
                                    name="confirmNewPassword"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field: { value, onChange, onBlur } }) => (
                                        <FormControl fullWidth size="small">
                                            <InputLabel htmlFor="account-settings-confirm-new-password">
                                                New Password
                                            </InputLabel>
                                            <OutlinedInput
                                                size="small"
                                                label="New Pasword"
                                                value={value}
                                                id="account-settings-confirm-new-password"
                                                type={values.showConfirmNewPassword ? 'text' : 'password'}
                                                onChange={onChange}
                                                onBlur={onBlur}
                                                endAdornment={
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="toggle password visibility"
                                                            onClick={handleClickShowConfirmNewPassword}
                                                            onMouseDown={handleMouseDownConfirmNewPassword}
                                                        >
                                                            {values.showConfirmNewPassword ? (
                                                                <EyeOutline />
                                                            ) : (
                                                                <EyeOffOutline />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }
                                            />
                                        </FormControl>
                                    )}
                                />
                                {errors.confirmNewPassword && (
                                    <FormHelperText sx={{ color: 'error.main' }}>
                                        {errors.confirmNewPassword.message}
                                    </FormHelperText>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid
                        item
                        sm={6}
                        xs={12}
                        sx={{ display: 'flex', mt: [7.5, 2.5], alignItems: 'center', justifyContent: 'center' }}
                    >
                        <img width={183} alt="avatar" height={256} src="/images/pages/pose-m-1.png" />
                    </Grid>
                </Grid>
            </CardContent>

            <Divider sx={{ mt: 0, mb: 1.75 }} />

            <CardContent>
                {/*<Box sx={{ mb: 5.75, display: 'flex', alignItems: 'center' }}>*/}
                {/*    <KeyOutline sx={{ mr: 3 }} />*/}
                {/*    <Typography variant='h6'>Two-factor authentication</Typography>*/}
                {/*</Box>*/}

                {/*<Box sx={{ mb: 11, display: 'flex', justifyContent: 'center' }}>*/}
                {/*    <Box*/}
                {/*        sx={{*/}
                {/*            maxWidth: 368,*/}
                {/*            display: 'flex',*/}
                {/*            textAlign: 'center',*/}
                {/*            alignItems: 'center',*/}
                {/*            flexDirection: 'column'*/}
                {/*        }}*/}
                {/*    >*/}
                {/*        <CustomAvatar skin='light' variant='rounded' sx={{ mb: 3.5, width: 48, height: 48 }}>*/}
                {/*            <LockOpenOutline sx={{ fontSize: '1.75rem' }} />*/}
                {/*        </CustomAvatar>*/}
                {/*        <Typography sx={{ fontWeight: 600, mb: 3.5 }}>Two factor authentication is not enabled yet.</Typography>*/}
                {/*        <Typography variant='body2'>*/}
                {/*            Two-factor authentication adds an additional layer of security to your account by requiring more than just*/}
                {/*            a password to log in. Learn more.*/}
                {/*        </Typography>*/}
                {/*    </Box>*/}
                {/*</Box>*/}

                <ApiKeyManager />

                <StandardButton
                    sx={{ mr: 3.5, ml: 3.5 }}
                    variant="contained"
                    onClick={handleSubmit(onSave)}
                    icon={<SaveAltIcon />}
                >
                    Save Changes
                </StandardButton>
            </CardContent>
        </form>
    );
};
export default TabSecurity;
